import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../../services/api.service';
import './GalleryPhotos.css';

const GalleryPhotos = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    status: '1',
    photo: null,
    preview: null
  });
  const [uploading, setUploading] = useState(false);
  const [editModal, setEditModal] = useState({
    show: false,
    photo: null,
    title: '',
    status: '1',
    newPhoto: null,
    preview: null
  });
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchGalleryAndPhotos();
  }, [id]);

  const fetchGalleryAndPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch gallery details
      const galleryResponse = await apiService.getGalleryById(id);
      if (galleryResponse.data.success) {
        const data = galleryResponse.data.data;
        setGallery(data.category || data);
      } else {
        setError('Failed to load gallery');
        return;
      }

      // Fetch photos
      try {
        const photosResponse = await apiService.getGalleryPhotos(id);
        if (photosResponse.data.success) {
          const photosData = photosResponse.data.data;
          // Ensure we always set an array
          setPhotos(Array.isArray(photosData) ? photosData : []);
        } else {
          setPhotos([]);
        }
      } catch (photoErr) {
        console.error('Error fetching photos:', photoErr);
        setPhotos([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load gallery');
      console.error('Gallery fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEdit) {
          setEditModal(prev => ({
            ...prev,
            newPhoto: file,
            preview: reader.result
          }));
        } else {
          setUploadForm(prev => ({
            ...prev,
            photo: file,
            preview: reader.result
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.photo) {
      setError('Please select a photo');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('photo', uploadForm.photo);
      formData.append('title', uploadForm.title);
      formData.append('status', uploadForm.status);

      await apiService.uploadGalleryPhoto(id, formData);
      
      setSuccessMessage('Photo uploaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Reset form
      setUploadForm({
        title: '',
        status: '1',
        photo: null,
        preview: null
      });

      // Refresh photos
      fetchGalleryAndPhotos();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload photo');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await apiService.deleteGalleryPhoto(id, photoId);
      setSuccessMessage('Photo deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh photos
      fetchGalleryAndPhotos();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete photo');
      console.error('Delete error:', err);
    }
  };

  const openEditModal = (photo) => {
    setEditModal({
      show: true,
      photo: photo,
      title: photo.image_title,
      status: photo.status,
      newPhoto: null,
      preview: null
    });
  };

  const closeEditModal = () => {
    setEditModal({
      show: false,
      photo: null,
      title: '',
      status: '1',
      newPhoto: null,
      preview: null
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    try {
      let data;
      
      if (editModal.newPhoto) {
        // If new photo is selected, use FormData
        data = new FormData();
        data.append('photo', editModal.newPhoto);
        data.append('title', editModal.title);
        data.append('status', editModal.status);
      } else {
        // Otherwise just update title and status
        data = {
          title: editModal.title,
          status: editModal.status
        };
      }

      await apiService.updateGalleryPhoto(id, editModal.photo.photo_id, data);
      
      setSuccessMessage('Photo updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      closeEditModal();
      fetchGalleryAndPhotos();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update photo');
      console.error('Update error:', err);
    }
  };

  const handleBack = () => {
    navigate('/admin/galleries');
  };

  if (loading) {
    return (
      <div className="gallery-photos-container">
        <div className="loading">Loading gallery photos...</div>
      </div>
    );
  }

  if (error && !gallery) {
    return (
      <div className="gallery-photos-container">
        <div className="error-container">
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
          <button className="btn btn-primary" onClick={handleBack}>
            Back to Galleries
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-photos-container">
      <div className="gallery-photos-header">
        <div>
          <h2>{gallery?.category_title || 'Gallery Photos'}</h2>
          <p className="gallery-location">
            {gallery?.category_location && (
              <>📍 {gallery.category_location}</>
            )}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={handleBack}>
          ← Back to Galleries
        </button>
      </div>

      {successMessage && (
        <div className="alert alert-success">
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Upload Form */}
      <div className="upload-section">
        <h3>Upload New Photo</h3>
        <form onSubmit={handleUpload} className="upload-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="photo">Photo *</label>
              <input
                type="file"
                id="photo"
                accept="image/*"
                onChange={(e) => handleFileChange(e, false)}
                required
              />
              {uploadForm.preview && (
                <div className="image-preview">
                  <img src={uploadForm.preview} alt="Preview" />
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder="Enter photo title (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={uploadForm.status}
                onChange={(e) => setUploadForm({ ...uploadForm, status: e.target.value })}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload Photo'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Photos Grid */}
      {!Array.isArray(photos) || photos.length === 0 ? (
        <div className="no-photos">
          <div className="no-photos-content">
            <h3>No Photos Yet</h3>
            <p>Upload your first photo using the form above.</p>
          </div>
        </div>
      ) : (
        <div className="photos-grid">
          {photos.map((photo) => (
            <div key={photo.photo_id} className="photo-card">
              <img 
                src={photo.gallry_photo || 'https://via.placeholder.com/300'} 
                alt={photo.image_title || 'Gallery photo'}
              />
              <div className="photo-info">
                <div className="photo-details">
                  <h4>{photo.image_title || 'Untitled'}</h4>
                  <span className={`status-badge ${photo.status === '1' ? 'active' : 'inactive'}`}>
                    {photo.status === '1' ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="photo-actions">
                  <button 
                    className="btn-icon btn-edit" 
                    onClick={() => openEditModal(photo)}
                    title="Edit photo"
                  >
                    ✏️
                  </button>
                  <button 
                    className="btn-icon btn-delete" 
                    onClick={() => handleDelete(photo.photo_id)}
                    title="Delete photo"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editModal.show && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Photo</h3>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            <form onSubmit={handleEdit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Current Photo</label>
                  <div className="current-photo">
                    <img 
                      src={editModal.preview || editModal.photo?.gallry_photo} 
                      alt={editModal.photo?.image_title} 
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-photo">Replace Photo (optional)</label>
                  <input
                    type="file"
                    id="edit-photo"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, true)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-title">Title</label>
                  <input
                    type="text"
                    id="edit-title"
                    value={editModal.title}
                    onChange={(e) => setEditModal({ ...editModal, title: e.target.value })}
                    placeholder="Enter photo title"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-status">Status</label>
                  <select
                    id="edit-status"
                    value={editModal.status}
                    onChange={(e) => setEditModal({ ...editModal, status: e.target.value })}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPhotos;

